import React from "react";

import {
  FormControl,
  Input,
  FormHelperText,
  MuiThemeProvider,
  createTheme,
} from "@material-ui/core";
import styled from "styled-components";
import { withTheme } from "@material-ui/styles";
import { formConnector } from "../../FormConfig";
import Text from "../strings/Text";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { IconButton } from "@material-ui/core";
import { FontStyles } from "../index";
import { Spacing } from "../../config";

const Label = styled(Text)(({ theme, $witherror, $color, $disabled }) => {
  const { palette: colors } = theme;
  return {
    ...FontStyles.semibold14,
    textTransform: "uppercase",
    position: "relative",
    top: 0,
    left: 0,
    color: $witherror
      ? colors.error.main
      : $color === 2
      ? colors.text.primary
      : $color === 1
      ? colors.orange
      : colors.text.secondary,
    transition: ".2s",
    pointerEvents: "none",
    alignItems: "center",
    display: "flex",
    overflow: "hidden",
    opacity: $disabled ? 0.4 : 1,
  };
});

const Background = styled.div(
  ({ theme, $minHeight, $error, disabled, maxHeight }) => {
    const { spacing, palette: colors } = theme;
    return {
      border: $error && `1px solid ${colors.error.main}`,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      backgroundColor: colors.background.input,
      paddingLeft: spacing(1.5),
      boxShadow: $error
        ? `inset 0 0 0 2px ${colors.text.tertiary}`
        : `inset 0 0 0 ${colors.text.tertiary}`,
      opacity: disabled ? 0.3 : 1,
      borderRadius: 5,
      minHeight: $minHeight && spacing(12),
      maxHeight: maxHeight && spacing(maxHeight),
    };
  }
);

const StyledInput = styled(Input)(
  ({
    theme,
    error,
    disabled,
    maxHeight,
    $inputStyle,
    $error,
    $small,
    $alternativecolors,
    count,
    ...rest
  }) => {
    const { spacing, palette: colors } = theme;
    return {
      display: count && "flex",
      alignItems: count && "flex-start",
      minHeight: count && spacing(22.5),
      padding: $small ? spacing(0, 1) : spacing(1),
      multiline: true,
      textOverflow: "ellipsis",
      color: colors.text.primary,
      backgroundColor:
        $alternativecolors === 2
          ? colors.gray
          : $alternativecolors
          ? colors.text.primary
          : "transparent",
      borderRadius: 5,
      ...FontStyles.medium16,
      maxHeight: maxHeight && spacing(maxHeight),
      overflow: maxHeight && "auto",

      "& .MuiInput-input": {
        color: $alternativecolors ? colors.text.primary : colors.text.secondary,
        width: spacing(5),

        "&::placeholder": {
          opacity: 0.8,
        },
        "&input:-internal-autofill-selected": {
          color: "red",
          background: "red",
        },
        "& .MuiInputBase-input": {
          color: "#6605B8",
          maxHeight: maxHeight && spacing(maxHeight),
          overflow: maxHeight && "auto",
        },
      },
      "& .MuiInputBase-input": {
        color: "#6605B8",
        maxHeight: maxHeight && spacing(maxHeight),
        overflow: maxHeight && "auto",
      },
      ...$inputStyle,
    };
  }
);
const Icon = styled(IconButton)(({ theme }) => {
  const { palette: colors, spacing } = theme;
  return {
    position: "absolute",
    right: spacing(2),
    top: spacing(4.75),
    color: colors.text.primary,
    width: spacing(2),
    height: spacing(2),
    padding: 0,
  };
});
const Count = styled(Text)(({ theme, $withValue }) => {
  const { spacing, palette: colors } = theme;

  return {
    width: "100%",
    color: colors.text.primary,
    opacity: 0.6,
    textAlign: "end",
    marginBlock: spacing(0.5),
    paddingRight: spacing(1.5),
    bottom: spacing(-1),
    position: "absolute",
    ...FontStyles.medium12,
  };
});
const LabelView = styled.div(({ theme }) => {
  return {
    display: "flex",
    alignContent: "center",
    justifyContent: "flex-start",
  };
});
const ErrorMessage = styled(FormHelperText)(({ theme }) => {
  const { palette: colors } = theme;
  return {
    ...FontStyles.medium12,
    color: colors.text.tertiary,
  };
});

const inputTheme = createTheme({
  overrides: {
    MuiInputBase: {
      input: {
        width: "4.8rem",
      },
    },
  },
});

function InputComponent(props) {
  const {
    value,
    error,
    handleKeyPress,
    handleKeyDown,
    onBlur,
    setConfigs,
    ref,
    formHandler,
    fileInputRef,
    usedProps,
  } = formConnector.useStringValue(props);

  const {
    inputLabel,
    icon,
    theme,
    required,
    color,
    disableError,
    hideVisualError,
    margin = "none",
    variant = "filled",
    inputStyle,
    type,
    readOnly,
    autoComplete = false,
    small,
    alternativeColors,
    withHide,
    placeholder,
    percentage,
    count,
    minHeight,
    maxHeight,
    withUnderLine,
    multiline,
    requiredField,
    forceError,
    disabled,
    ...rest
  } = usedProps;

  return (
    <FormControl fullWidth error={hideVisualError ? null : Boolean(error)}>
      {inputLabel && (
        <LabelView>
          <Label
            $disabled={disabled}
            $withValue={Boolean(value) || value === 0 || Boolean(placeholder)}
            $witherror={Boolean(error) ? true : undefined}
            $color={color}
          >
            {inputLabel}
            {Boolean(required) && "*"}
          </Label>
        </LabelView>
      )}

      <MuiThemeProvider theme={percentage ? inputTheme : theme}>
        <Background
          $error={hideVisualError ? null : Boolean(error)}
          disabled={props.disabled || type === "file"}
          $minHeight={minHeight}
          maxHeight={maxHeight}
        >
          <StyledInput
            {...rest}
            maxHeight={maxHeight}
            multiline={count || multiline ? true : false}
            count={count || multiline}
            inputProps={{
              disableUnderline: true,
              maxLength: count,
              style: {
                maxHeight: maxHeight && Spacing(maxHeight - 1.5),
                overflow: maxHeight && "auto",
              },
            }}
            $alternativecolors={alternativeColors}
            $small={small}
            disableUnderline={true}
            autoComplete={autoComplete ? autoComplete : null}
            readOnly={readOnly}
            disabled={props.disabled || type === "file"}
            $error={hideVisualError ? null : Boolean(error)}
            onClick={
              type === "file" ? () => fileInputRef.current.click() : null
            }
            type={type === "file" ? null : type}
            value={type === "file" ? (value ? value.name : "") : value || ""}
            required={Boolean(required)}
            variant={variant}
            label={value ? inputLabel : null}
            fullWidth
            margin={margin}
            onKeyPress={handleKeyPress}
            onKeyDown={handleKeyDown}
            inputRef={ref}
            onChange={
              type !== "file" ? (event) => setConfigs(event.target.value) : null
            }
            onBlur={(event) => {
              setTimeout(() => {
                onBlur(event);
                formHandler.testInputError(props.name);
              }, 10);
            }}
            placeholder={placeholder}
          />
          {count ? (
            <Count>
              {ref.current ? ref.current.value.length : 0} / {count} caracteres
            </Count>
          ) : null}
        </Background>
      </MuiThemeProvider>

      {disableError ? null : (
        <ErrorMessage error={forceError || Boolean(error)}>
          {forceError ? forceError : Boolean(error) ? error : " "}
        </ErrorMessage>
      )}

      {withHide ? (
        <Icon onClick={withHide} component="span">
          <VisibilityIcon />
        </Icon>
      ) : null}
    </FormControl>
  );
}

export default withTheme(InputComponent);
